import apiClient from "./apiClient";

export interface CategoryListItem {
  id: string;
  name: string;
  description?: string | null;
  unit_price_minor?: number | null;
  is_archived?: boolean;
  items_count?: number;
}

export interface CategoryDetail extends CategoryListItem {
  company_id: string;
  created_at?: string;
  updated_at?: string;
}

export interface ListCategoriesOptions {
  q?: string;
  includeArchived?: boolean;
  page?: number;
  pageSize?: number;
}

export interface ListCategoriesResponse {
  categories: CategoryListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface UpdateCategoryPayload {
  name?: string;
  description?: string | null;
  unit_price_minor?: number | null;
  is_archived?: boolean;
}

export interface CategoryMonthlySalesPoint {
  month: string;
  revenue_minor: number;
}

export interface CategoryStatsPayload {
  revenue_total_minor: number;
  monthly_sales: CategoryMonthlySalesPoint[];
}

export async function listCategories(
  companyId: string,
  options: ListCategoriesOptions = {},
): Promise<ListCategoriesResponse> {
  const params = new URLSearchParams();
  if (options.q) params.set("q", options.q);
  if (options.includeArchived) params.set("includeArchived", "true");
  params.set("page", String(options.page || 1));
  params.set("pageSize", String(options.pageSize || 20));

  const response = await apiClient.get(
    `/api/companies/${companyId}/categories?${params.toString()}`,
  );

  return response as ListCategoriesResponse;
}

export async function getCategory(
  companyId: string,
  categoryId: string,
  options: { includeArchived?: boolean } = {},
): Promise<CategoryDetail> {
  const params = new URLSearchParams();
  if (options.includeArchived) {
    params.set("includeArchived", "true");
  }

  const query = params.toString();
  const response = await apiClient.get(
    `/api/companies/${companyId}/categories/${categoryId}${query ? `?${query}` : ""}`,
  );

  return response.category as CategoryDetail;
}

export async function getCategoryStats(
  companyId: string,
  categoryId: string,
): Promise<CategoryStatsPayload> {
  const response = await apiClient.get(
    `/api/companies/${companyId}/categories/${categoryId}/stats`,
  );

  return response.stats as CategoryStatsPayload;
}

export async function updateCategory(
  companyId: string,
  categoryId: string,
  payload: UpdateCategoryPayload,
): Promise<CategoryDetail> {
  const response = await apiClient.put(
    `/api/companies/${companyId}/categories/${categoryId}`,
    payload,
  );

  return response.category as CategoryDetail;
}

export interface CreateCategoryPayload {
  name: string;
  description?: string | null;
  unit_price_minor?: number | null;
}

export async function createCategory(
  companyId: string,
  payload: CreateCategoryPayload,
): Promise<CategoryDetail> {
  const response = await apiClient.post(
    `/api/companies/${companyId}/categories`,
    payload,
  );

  return response.category as CategoryDetail;
}

const categoriesService = {
  listCategories,
  getCategory,
  updateCategory,
  getCategoryStats,
  createCategory,
};

export default categoriesService;
